import { Alert, Box, darken, lighten, Stack } from "@mui/material";
import Link from "next/link";
import React from "react";
import { MdInfoOutline } from "react-icons/md";
import { SSButton } from "src/components/base/components/SSButton";
import { SSText } from "src/components/base/components/SSText";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useMarketplaceSeller } from "src/components/feature/marketplace/seller/useMarketplaceSeller";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { routes } from "src/shared/routes";
import { TRegistrationCardData } from "src/shared/types/globalStotre.types";
import { TTeamConfig } from "src/shared/types/teamConfig.types";
import { createJsxLink } from "src/utils/create-jsx-link";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Gators Student Ticket Offers",
    description: (
      <>
        Purchase single game & guest tickets below! Limited quantities of single
        game student and guest tickets for LIU and USF will be available
        starting at 10 am on Monday, August 4th.
        <br />
        <br />
        All other home games will go on sale no later than 2 weeks prior to that
        home game, based on availability.
      </>
    ),
    buttonLabel: "Buy Tickets",
    buttonVariant: "contained",
    authRedirectPage: routes.home,
  },
  {
    title: "Gators Student Registration",
    description:
      "Register your student football pass & link up to create groups to sit with your friends! Your unique link is located in your account. Please note seats are assigned the Friday before gameday. Additional guest tickets will be available for purchase. You must use the email address you purchased your pass with to login.",
    authRedirectPage: routes.my_group,
    buttonLabel: "Register Your Account",
    buttonVariant: "contained",
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "Tickets will be delivered to your Florida Gators Account Manager the day before the game.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Yes, you can view your upcoming events to see all games that you are eligible for a ticket to.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "You will be notified tickets are available the day before the game via text message.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will be in the student section.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact the Gators box office for any assistance you may require.",
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "gators_students" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined on
        the Monday before gameday.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. You will receive an email confirming any guest ticket on-sale & availability.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: "An active purchase is not refundable.",
  },
];

export const TERMS_AND_CONDITION = () => {
  return (
    <>
      By checking this box you agree to receive SMS alerts regarding ticket
      delivery and agree to the{" "}
      {createJsxLink("Terms of Use", "https://www.attend.tech/terms-of-use")}{" "}
      and{" "}
      {createJsxLink(
        "Privacy Policy",
        "https://www.attend.tech/privacy-policy"
      )}
      .
    </>
  );
};

export const SWAF_INFO_TEXT = () => (
  <Stack
    p={1}
    my={2}
    gap={1}
    borderRadius={2}
    bgcolor="#CEE8F0"
    alignItems="center"
    flexDirection="row"
    flexWrap="nowrap"
  >
    <MdInfoOutline
      color="#014361"
      style={{
        flexShrink: 0,
        fontSize: 22,
        minWidth: 24,
        minHeight: 24,
      }}
    />
    <SSText
      variant="body2"
      color="#014361"
      sx={{
        wordBreak: "break-word",
        fontSize: { xs: "1rem", sm: "1rem" },
        lineHeight: 1.4,
        fontWeight: 400,
        display: "inline",
      }}
    >
      Create groups with eligible students — you&apos;ll only be seated together
      for games you&apos;ve bought tickets to.
    </SSText>
  </Stack>
);

export const FOOTER_LINKS = () => (
  <>
    <Link
      target="_blank"
      href="https://www.attend.tech/terms-of-use"
      style={{ color: "white " }}
    >
      Terms of Use
    </Link>{" "}
    <Link
      target="_blank"
      href="https://www.attend.tech/privacy-policy"
      style={{ color: "white " }}
    >
      Privacy Policy
    </Link>
  </>
);

export const CHECKOUT_TERMS_AND_CONDITION = () => {
  return (
    <>
      By checking this box you agree to receive SMS alerts regarding ticket
      delivery and agree to the{" "}
      <Link target="_blank" href="https://www.attend.tech/terms-of-use">
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link target="_blank" href="https://www.attend.tech/privacy-policy">
        Privacy Policy
      </Link>
      .
    </>
  );
};

export const BUYER_HOW_IT_WORKS = [
  {
    title: 'Step 1: Select "Purchase a Student Ticket"',
  },
  {
    title:
      "Step 2: Log in with the email address associated with your University of Florida Account Manager.",
  },
  {
    title: "Step 3: Select the game you are interested in attending.",
  },
  {
    title: "Step 4: Select a listing you wish to purchase.",
  },
  {
    title: "Step 5: Complete the purchase of your ticket.",
  },
  {
    title:
      "Step 6: Navigate to My Groups and be sure to create or join a group to be seated with your friend(s) for the game!",
  },
  {
    description: (
      <>
        You will be notified via text when your tickets for the game are
        available to be managed on Friday before gameday.
        <br />
        <br />
        All sales are final. No refunds will be issued.
      </>
    ),
  },
];

export const SELLER_HOW_IT_WORKS = [
  {
    title: 'Step 1: Select "Become a Seller"',
  },
  {
    title:
      "Step 2: Log in with the email address associated with your University of Florida Account Manager.",
  },
  {
    title: "Step 3: Select the game you wish to sell.",
  },
  {
    title: "Step 4: Complete the Seller Onboarding process.",
  },
  {
    title: "Step 5: Enter the number of tickets you wish to list.",
  },
  {
    title: (
      <>
        Step 6: Set your listing price for the tickets and click &quot;List for
        Resale&quot;.
        <br />
        <br />
      </>
    ),
  },

  {
    title: "Congrats! Your listing is complete",
    description: (
      <>
        If your ticket is purchased, you will be notified via text. Check back
        in on the portal at any time to view or update the status of your
        listing.
        <br />
        <br />
        If plans change and you can attend the game, be sure to remove your
        listing from the marketplace. If your ticket is purchased, no refunds
        will be issued.
        <br />
        <br />
        Please wait 5 business days for payouts to reflect in your bank
        statement prior to reaching out to support by clicking on Chat with
        Support in the right hand hamburger menu.
        <br />
        <br />
        All sales are final.
        <br />
        <br />
        Unsold tickets will be removed from the Marketplace. If your ticket does
        not sell, you will receive your barcodes Friday before gameday.
      </>
    ),
  },
];

export function MARKETPLACE_FAQS(teamSchema: string) {
  return [
    {
      question: "What is the Student Marketplace?",
      answer: (
        <>
          The Student Marketplace is the{" "}
          <b>only official, university-approved</b> platform for UF students to
          securely <b> buy and sell student football tickets</b>. It ensures
          that tickets stay within the student community and helps protect your
          ticket privileges.
        </>
      ),
    },
    {
      question: "Who can use the Student Marketplace?",
      answer: (
        <>
          Only <b>University of Florida students</b> can access the platform
          using the email associated with your UF Account Manager.
        </>
      ),
    },
    {
      question: "Can I sell my student ticket elsewhere?",
      answer: (
        <>
          No. Selling your student ticket on any other platform is not allowed
          and may result in the <b>loss of your student ticket</b>.
        </>
      ),
    },
    {
      question: "When can I list my ticket?",
      answer: (
        <>
          Listings can be created at any time,{" "}
          <b>
            but all sales must be finalized by 12:00 PM on the Wednesday before
            gameday.
          </b>
        </>
      ),
    },
    {
      question: "What if I change my mind after listing my ticket?",
      answer: (
        <>
          You can remove your ticket from the Marketplace at any time{" "}
          <b>before it is purchased</b>. If your ticket is purchased, the sale
          is <b>final</b> and <b>cannot be reversed or refunded</b>, even if
          your plans change.
        </>
      ),
    },
    {
      question: "When will I receive my ticket(s)?",
      answer: (
        <>
          Tickets will be available to manage on the Friday before gameday, and
          you will be notified via text.
        </>
      ),
    },
    {
      question: "When will I receive my payout?",
      answer: (
        <>
          If your ticket is sold, your payout is triggered immediately, but
          please allow 5 business days for it to reflect on your bank statement.
        </>
      ),
    },
    {
      question: "Can I sit with friends if I buy a ticket?",
      answer: (
        <>
          Yes. After purchasing a ticket, navigate to the{" "}
          <Link
            className="global-link"
            href={{
              pathname: routes.my_group,
              query: { teamSchema },
            }}
          >
            My Groups
          </Link>{" "}
          section and <b>create or join</b> a group to be seated with your
          friends. Group submissions must be finalized by{" "}
          <b>12:00 PM on the Wednesday before gameday.</b>
        </>
      ),
    },
    {
      question: "Are refunds available?",
      answer: (
        <>
          No. <b>All sales are final</b> for both buyers and sellers. Please
          make sure of your plans before buying or listing a ticket.
        </>
      ),
    },
    {
      question: "How does My Groups work?",
      answer: (
        <>
          The{" "}
          <Link
            className="global-link"
            href={{
              pathname: routes.my_group,
              query: { teamSchema },
            }}
          >
            My Groups
          </Link>{" "}
          feature is open continuously, allowing you to create or join a group
          at any time. However,{" "}
          <b>
            group formation locks at 12:00 PM EST on the Wednesday before
            gameday
          </b>{" "}
          for that week. <br />
          <br />
          If you create or update a group <b>after Wednesday at 12:00 PM EST</b>
          , it will <b>only apply to the following game</b>, not the upcoming
          one. Be sure to finalize your group before the deadline to be seated
          with your friends.
        </>
      ),
    },
    {
      question: "How do I contact support?",
      answer: (
        <>
          If you need help, click on <b>Chat with Support</b> in the menu icon
          (three line logo) at the top-right corner.
          <br />
          <br />
          Support is available{" "}
          <b>Monday through Friday, 9:00 AM to 5:00 PM EST</b>, and during
          <b>gamedays</b> to assist with urgent questions.
        </>
      ),
    },
  ];
}

export function HomePageMarketplaceZeroStateDescription() {
  const theme = useSSTheme();
  const router = useRouterExt();
  const { marketplaceRedirectWarnModal } = useMarketplaceSeller();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const onClickMarketplace = async (ev: any) => {
    ev.preventDefault();
    setIsRedirecting(true);
    const warningRes = await marketplaceRedirectWarnModal();
    if (warningRes === "cancelled") return setIsRedirecting(false);
    await router.pushSchema(routes.marketplace_events);
    setIsRedirecting(false);
  };

  return (
    <SSText
      fontSize={"1rem"}
      fontWeight={500}
      color={theme.mui.palette.grey[700]}
    >
      You don’t have student tickets for any upcoming games. Check out the{" "}
      <Link className="global-link" onClick={onClickMarketplace} href={""}>
        Student Marketplace
      </Link>{" "}
      to purchase a ticket from another student.
    </SSText>
  );
}

export const ListingCheckoutTermsAndCondition = () => {
  return (
    <>
      By checking this box you have read and agree to Attend’s{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>
      ,{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>{" "}
      and{" "}
      <Link
        className="global-link"
        target="_blank"
        href="http://attend.tech/marketplace-purchase-policy "
      >
        Purchase Policy
      </Link>
      .
    </>
  );
};

export const CreateListingTermsAndCondition = () => {
  return (
    <>
      By clicking “Create Listing”, you have read and agree to Attend’s{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>
      ,{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>{" "}
      and{" "}
      <Link
        className="global-link"
        target="_blank"
        href="http://attend.tech/marketplace-purchase-policy "
      >
        Purchase Policy
      </Link>
      .
    </>
  );
};
